import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/configs/app.config';
import { DestroyNotifierComponent } from 'src/app/core/component/destroy/destroy-notifier.component';
import { Status } from 'src/app/core/services/http/general/enums/status';
import { FormGroupService } from '../form-error/form-group.service';
import { getFormGroup } from './form-group/form.group';
import { SchoolYearRegistry } from '../../core/services/date-manager/school-year/school-year.registry';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationModal } from '../confirmation-modal/interfaces/confirmation.modal';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { EventType } from 'src/app/constants/events-type';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export class FilterComponent extends DestroyNotifierComponent implements OnInit {
  @Input() shouldShowOnlyFilters = false;
  @Input() shouldBeVisibleBuildInInputFilter = true;
  @Input() shouldBeVisibleEmbeddedFilters = true;
  @Input() shouldBeVisibleRangeFilter = true;
  @Input() shouldBeVisibleStatus = true;
  @Input() shouldBeVisibleToggleExtraFilterButton = true;
  @Input() customClass = '';
  @Input() filter: Record<string, any> | undefined;

  appliedFilterValue: Record<string, any> = {};
  // Sets external form controls to the form
  @Input() set controls(controls: Record<string, any> | undefined) {
    if (!controls) {
      return;
    }

    for (const controlName in controls) {
      if (Object.prototype.hasOwnProperty.call(controls, controlName)) {
        this.form.addControl(controlName, controls[controlName]);
      }
    }
  }

  @Output() filterChange = new EventEmitter<Record<string, any>>();
  @Output() filterCollapse = new EventEmitter<boolean>();

  form = new UntypedFormBuilder().group({ filter: [], status: [] });
  initialFormValue: any;
  initialFilterValue: any;
  hasUnsavedChanges = false;
  userHasMadeChanges = false; // Track if user has made any changes
  isRestoringForm = false; // Flag to prevent setting userHasMadeChanges during form restoration
  previousDateValue: any;
  changedFieldsCount = 0;
  
  appConfig = AppConfig;
  isCollapsed = true;
  debounce: any;
  status = Status;
  lastSentFilterValue = '';
  formChange: { change: true } | undefined;
  bsConfig = Object.assign(AppConfig.BsConfig, {
    maxDate: this.schoolYearRegistry.selectedSchoolYear.endDate,
    minDate: this.schoolYearRegistry.selectedSchoolYear.startDate
  });

  constructor(public cd: ChangeDetectorRef, private modalService: BsModalService,private translate: TranslateService, public formGroupService: FormGroupService, private schoolYearRegistry: SchoolYearRegistry, private eventManager: EventManagerService) {
    super();
  }

  ngOnInit(): void {
    if (this.shouldBeVisibleRangeFilter) {
      this.addDefaultFormControls();
    }

    this.subscribeToFormChanges();

    this.patchFormValues();

    // Don't subscribe to form changes here - will be controlled by onCollapse method
    this.initialFilterValue = this.filter ?? {};
    const result: any = this.convertFormControlToRecord();
    this.changedFieldsCount = this.countNonEmptyFields(result);
  }

  patchFormValues(): void {
    if (this.filter) {
      const patchableKeys: Record<string, any> = {};
    
      for (const key in this.filter) {
        const hasControl = this.form.controls.hasOwnProperty(key);
        const isVisible =
          (key === 'filter' && this.shouldBeVisibleBuildInInputFilter) ||
          ((key === 'startDate' || key === 'endDate') && this.shouldBeVisibleRangeFilter) ||
          (key === 'status' && this.shouldBeVisibleStatus);
    
        if (hasControl && isVisible) {
          // For date fields, ALWAYS use calendar dates from filter input when they exist
          // This overrides any existing form control values to show calendar selected dates
          if ((key === 'startDate' || key === 'endDate') && this.shouldBeVisibleRangeFilter) {
            if (this.filter[key] !== null && this.filter[key] !== undefined) {
              patchableKeys[key] = this.filter[key];
            }
          } else {
            // For non-date fields, patch normally
            patchableKeys[key] = this.filter[key];
          }
        }
      }
    
      if (Object.keys(patchableKeys).length > 0) {
        this.form.patchValue(patchableKeys);
      }
    }
  }

  onClearFilters(): void {
    const current = this.convertFormControlToRecord();
    const isAlreadyEmpty = Object.values(current ?? {}).every(v => v === null || v === undefined || v === '');

    // If there were values before clearing, mark as unsaved changes
    if (!isAlreadyEmpty) {
      this.hasUnsavedChanges = true;
      this.userHasMadeChanges = true; // User made changes by clearing
    }

    this.formGroupService.clearFormControlsValuesByFormGroup(this.form, false, false);
    this.formChange = { change: true };
    this.cd.detectChanges();
    
    // Reset applied state but preserve hasUnsavedChanges and keep the count unchanged until apply is clicked
    this.resetAppliedState(false);
    
    // Force hasUnsavedChanges to true after clearing to enable apply button
    this.hasUnsavedChanges = true;
    this.userHasMadeChanges = true; // User made changes by clearing
    
    this.emitFilterChange();
  }

  onCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.filterCollapse.emit(this.isCollapsed);
    
    if (!this.isCollapsed) {
      // Panel is now expanded - subscribe to form changes
      this.subscribeToFormChanges();
      this.patchFormValues();
      
      // Reset userHasMadeChanges when expanding panel to start fresh
      this.userHasMadeChanges = false;
    } else {
      // Panel is now collapsed - unsubscribe from form changes
      this.unsubscribeFromFormChanges();
    }
  }

  private addDefaultFormControls(): void {
    const form = getFormGroup();

    for (const controlName in form.controls) {
      if (Object.prototype.hasOwnProperty.call(form.controls, controlName)) {
        this.form.addControl(controlName, form.controls[controlName]);
      }
    }
  }

  private subscribeToFormChanges(): void {
    // Subscribe to form changes only when panel is collapsed
    this.onInputChange();
    this.onFormChange();
  }

  private  unsubscribeFromFormChanges(): void {
    // Unsubscribe from form changes when panel is expanded
    this.destroyNotify.next();
    this.userHasMadeChanges = false;
  }

  private onInputChange(): void {
    this.form.controls.filter.valueChanges.subscribe((value: string) => {
      if(value !== null){
        this.onInputChangeHandler(value);
      }
    });
  }

  private onInputChangeHandler(value: string): void {
    // Stops further execution if min length of string is not reached
    // when we type new sumbols (increase length of input value)
    // otherwise this won't executed (decrease length of input value)
    if (value.length < AppConfig.Inputs.minTermLength && this.lastSentFilterValue.length < value.length) {
      return;
    }

    clearTimeout(this.debounce);
    // Emits select event after debounceTimeMs
    // Protects API from requesting a new data, every time when the user types a new symbol
    this.debounce = setTimeout(() => {
      // Prevents emitting of the event if the last send value is the same as current one
      // Example: If you type something and quickly deleted until debounceTimeMs.
      // This action wont be emitted because assigning of new value happend inside debounce.
      // This prevents API from additional requests that already is loaded
      if (this.lastSentFilterValue === value) {
        return;
      }

      this.lastSentFilterValue = value;

      const result:any = this.convertFormControlToRecord();
      this.filterChange.emit(result);
    }, AppConfig.AutoComplete.debounceTimeMs);
  }

  /**
   * Handles all form controls changes without input
   */
  onFormChange(): void {
    const merged = this.formGroupService.mergeValueChangesByFormControlWithoutExcluded(['filter'], this.form);

    if (!merged) {
      return;
    }

    merged.pipe(takeUntil(this.destroyNotify)).subscribe(() => {
      const currentDateValue = this.form.get('date')?.value;
      const currentValues = this.convertFormControlToRecord();

      // Special handling for date field changes - emit immediately for calendar components
      if (currentDateValue !== this.previousDateValue) {
        this.filterChange.emit(currentValues);
      }

      this.previousDateValue = currentDateValue;

      // Compare current form state with applied filter values
      const isSameAsApplied = this.isFilterValuesSameToInitialFilterValue(currentValues ?? {}, this.appliedFilterValue);

      // Check if there are any non-empty values in current form
      const hasCurrentValues = Object.values(currentValues ?? {}).some(v => v !== null && v !== undefined && v !== '');
      const hasAppliedValues = Object.values(this.appliedFilterValue).some(v => v !== null && v !== undefined && v !== '');

      // Enable apply button if:
      // 1. Current values are different from applied values, OR
      // 2. There are current values (new filters added), OR
      // 3. There are applied values but no current values (filters were cleared)
      this.hasUnsavedChanges = !isSameAsApplied || hasCurrentValues || hasAppliedValues;

      // Only set userHasMadeChanges to true when panel is expanded and not during form restoration
      if (!this.isCollapsed && !this.isRestoringForm) {
        this.userHasMadeChanges = true;
      }
      
      this.formChange = { change: true };
      this.cd.detectChanges();
    });
  }

  emitFilterChange(): void {
    const result:any = this.convertFormControlToRecord();
    
    const isFilterValuesSameToInitialFilterValue = this.isFilterValuesSameToInitialFilterValue(result, this.initialFilterValue);
    
    // Only reset hasUnsavedChanges if the current values match the initial filter values
    if(isFilterValuesSameToInitialFilterValue){
      this.hasUnsavedChanges = false;
    }

    this.formChange = { change: true };
  }

  convertFormControlToRecord(): Record<string, any> | undefined {
    const result: Record<string, any> | undefined = {};

    if (!this.form?.controls) {
      return result;
    }

    for (const key in this.form.controls) {
      if (Object.prototype.hasOwnProperty.call(this.form.controls, key)) {
        let value = this.form.controls[key].value;
        value = this.getAutoCompleteItemValue(value);
        result[key] = value; //Always assign
      }
    }

    // Handle date priority:
    // 1. If filter is collapsed → always use calendar dates (calendar is source of truth)
    // 2. If filter is expanded → use form controls if they have values (user edited), 
    //    otherwise use calendar dates to prevent showing current dates
    if (this.shouldBeVisibleRangeFilter && this.filter) {
      if (this.isCollapsed) {
        // Filter collapsed: always use calendar dates
        if (this.filter.startDate !== null && this.filter.startDate !== undefined) {
          result.startDate = this.filter.startDate;
        }
        if (this.filter.endDate !== null && this.filter.endDate !== undefined) {
          result.endDate = this.filter.endDate;
        }
      } else {
        // Filter expanded: use form controls if they have values (user edited), 
        // otherwise fallback to calendar dates to avoid showing current dates
        if ((result.startDate === null || result.startDate === undefined) && 
            this.filter.startDate !== null && this.filter.startDate !== undefined) {
          result.startDate = this.filter.startDate;
        }
        if ((result.endDate === null || result.endDate === undefined) && 
            this.filter.endDate !== null && this.filter.endDate !== undefined) {
          result.endDate = this.filter.endDate;
        }
      }
    }

    return result;
  }

  private getAutoCompleteItemValue(value: any): any {
    if (value && value.toString() === '[object Object]' && value.hasOwnProperty('value')) {
      return value.value;
    }

    return value;
  }

  applyFilters(): void {
    const result: any = this.convertFormControlToRecord();
    this.changedFieldsCount = this.countNonEmptyFields(result); // ✅ updated here
  
    this.appliedFilterValue = { ...this.initialFilterValue };
    this.hasUnsavedChanges = false;
    this.userHasMadeChanges = false; // Reset flag when user applies filters
    this.appliedFilterValue = { ...result };
    this.filterChange.emit(result);
    this.onCollapse();
    this.eventManager.broadcast(EventType.IsTableHeightUpdated);
  }
  
  async onCloseClick(): Promise<void> {
    if (this.userHasMadeChanges) {
      const shouldProceed = await this.showUnSavedChanges();
      
      if (!shouldProceed) {
        return;
      }

      // Apply the current filters
      this.applyFilters();

      // If the applied filter count is 0 (all filters cleared), reset userHasMadeChanges
      this.hasUnsavedChanges = false;
      this.userHasMadeChanges = false;
    }
  
    // Unsubscribe from form change observables to prevent memory leaks
    this.destroyNotify.next();
    this.destroyNotify.complete();

    this.isCollapsed = true;
    this.filterCollapse.emit(this.isCollapsed);
  }
  
  resetFormToInitialState(): void {
    if (!this.initialFilterValue || !this.form) return;
  
    for (const key in this.initialFilterValue) {
      if (this.form.controls[key]) {
        this.form.controls[key].setValue(this.initialFilterValue[key]);
      }
    }
  
    // Clear other fields not part of `initialFilterValue`
    for (const key in this.form.controls) {
      if (!this.initialFilterValue.hasOwnProperty(key) && key !== 'filter') {
        this.form.controls[key].setValue(null);
      }
    }
  
    // Update appliedFilterValue and reset hasUnsavedChanges
    this.appliedFilterValue = { ...this.initialFilterValue };
    this.hasUnsavedChanges = false;
    
    // Don't update changedFieldsCount here - it should only update on apply button click
    this.filterChange.emit(this.convertFormControlToRecord());
  }
  

  async showUnSavedChanges(): Promise<boolean> {
    const translations = await this.translate
      .get([
        'THERE_ARE_UNAPPLIED_CHANGES',
        'CONFIRM_TITLE',
        'OK',
        'CANCEL'
      ])
      .toPromise();
  
    return new Promise<boolean>((resolve) => {
      const initialState = {
        modal: {
          buttonText: translations.OK,
          cancelButtonText: translations.CANCEL,
          title: translations.CONFIRM_TITLE,
          body: translations.THERE_ARE_UNAPPLIED_CHANGES,
          callback: () => {
            resolve(true);
          },
          cancelCallback: () => {
            resolve(false);
          }
        } as ConfirmationModal
      };
  
      this.modalService.show(ConfirmationModalComponent, { initialState, class: 'prompt-modal', backdrop: 'static' });
    });
  }

  isFilterValuesSameToInitialFilterValue(objectA: Record<string, any>, objectB: Record<string, any>): boolean {
    for (const key in objectA) {
      if (objectB.hasOwnProperty(key)) {
        if (objectA[key] !== objectB[key]) {
          return false;
        }
      } else {
        if (objectA[key] !== null && objectA[key] !== undefined) {
          return false;
        }
      }
    }
  
    // Ensure no extra keys exist in B that are not in A
    for (const key in objectB) {
      if (!objectA.hasOwnProperty(key)) {
        return false;
      }
    }
  
    return true;
  }

  countNonEmptyFields(obj: Record<string, any>): number {
    let count = 0;
  
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
  
      const value = obj[key];
  
      //Always skip "date" field
      if (key === 'date') {
        continue;
      }
  
      //Conditionally skip based on visibility
      if (
        (key === 'filter' && !this.shouldBeVisibleBuildInInputFilter) ||
        ((key === 'startDate' || key === 'endDate') && !this.shouldBeVisibleRangeFilter) ||
        (key === 'status' && !this.shouldBeVisibleStatus)
      ) {
        continue;
      }
  
      if (value !== null && value !== undefined && value !== '') {
        count++;
      }
    }
  
    return count;
  }
  
  private resetAppliedState(resetUnsavedChanges = true): void {
    this.appliedFilterValue = {};
    if (resetUnsavedChanges) {
      this.hasUnsavedChanges = false;
      this.userHasMadeChanges = false; // Reset user changes flag
    }
    // Don't reset changedFieldsCount here - it should only update on apply button click
  }
  
  get shouldEnableClearButton(): boolean {
    const currentValues = this.form.getRawValue();

    // Check if there are any meaningful values in current form (excluding non-user-selectable controls)
    const hasCurrentValues = Object.entries(currentValues).some(([key, value]) => {
      // Skip non-user-selectable controls
      if (key === 'date') {
        return false;
      }

      // Handle different data types
      if (value === null || value === undefined) {
        return false;
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      if (Array.isArray(value)) {
        return value.length > 0 && value.some(item => item !== null && item !== undefined && item !== '');
      }

      if (typeof value === 'object') {
        return Object.keys(value).length > 0;
      }

      return true; // For other types (numbers, booleans, etc.)
    });

    // Enable clear button if there are current values OR user has made changes
    return hasCurrentValues || this.userHasMadeChanges;
  }
}
